import {
  FC, useCallback, useEffect, useMemo, type RefObject,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'next-i18next';
import classNames from 'classnames/bind';
import Icon from 'src/common/components/IconDS22';
import {
  useSearchInputContext, type SearchRefs,
} from 'src/common/components/Search/SearchInputProvider';
import ClearButton from 'src/common/components/Search/Inputs/ClearButton';
import { useSearchHeroModalContext } from 'src/common/components/Search/SearchHero/SearchHeroModalProvider';
import type { SearchHeroProps } from 'src/common/components/Search/SearchHero';
import { useSearchComboboxContext } from 'src/common/components/Search/SearchComboboxProvider';
import LoadingSpinner from 'src/common/components/LoadingSpinner';
import useAfterRenderCallback from 'src/common/hooks/useAfterRenderCallback';
import styles from '../Inputs.module.scss';

const cx = classNames.bind(styles);

export type InputLocationProps = {
  refs?: Partial<SearchRefs>;
  variant?: SearchHeroProps['variant'];
  popoverRef: RefObject<HTMLDivElement>;
};

const InputLocation: FC<InputLocationProps> = ({
  refs,
  variant,
  popoverRef,
}) => {
  const { filterLocation, submitSearch, setFilterLocation } = useSearchInputContext();
  const {
    close, openLocation, isLocationOpen, opening,
  } = useSearchHeroModalContext();
  const [shouldPickFirstMatch, setShouldPickFirstMatch] = useState(false);
  const [shouldSubmitFirstMatch, setSubmitFirstMatch] = useState(false);

  const { t } = useTranslation('common');

  const {
    combobox, id, places, isFetchingPlaces,
  } = useSearchComboboxContext();

  const optionalScrollIntoView = useAfterRenderCallback(() => {
    if (!combobox.isOpen) return;
    const items = popoverRef.current?.querySelectorAll('li');
    if (!items) return;
    const [highlightedItem] = Array.from(items).filter((item) => item.ariaSelected === 'true');
    if (!highlightedItem) return;

    const item = highlightedItem.getBoundingClientRect();
    const popover = popoverRef.current?.getBoundingClientRect();
    if (!popover) return;

    if (item.top < popover.top || item.bottom > popover.bottom) {
      // are child y bounds within popover bounds?
      highlightedItem.scrollIntoView({ block: 'nearest' });
    }
  });

  const submitAfter = useAfterRenderCallback(() => {
    submitSearch();
    close();
  });

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const match = places.find((place) => place.place_id === combobox.selectedItem?.place_id);

      const isRecentSearch = combobox.highlightedIndex >= places.length;

      // Rerun search when pressing enter on prefilled location
      if (!isFetchingPlaces && combobox.highlightedIndex === -1 && filterLocation?.place_id) {
        submitSearch();
        return;
      }

      // Choosing a place
      if (combobox.highlightedIndex >= 0 && !isRecentSearch) {
        const selection = places[combobox.highlightedIndex];
        setFilterLocation({
          place_id: selection.place_id,
          description: selection.description,
        });
        submitAfter();
      } else if (isFetchingPlaces || !match) {
        setShouldPickFirstMatch(true);
        setSubmitFirstMatch(true);
        return;
      }
    } else if (e.key !== 'Tab') {
      // Sometimes location is closed after a search, reopen when focused and editing
      openLocation();
      optionalScrollIntoView();
    }
    setShouldPickFirstMatch(false);
    setSubmitFirstMatch(false);
  }, [
    submitSearch,
    filterLocation?.place_id,
    submitAfter,
    combobox.highlightedIndex,
    places,
    isFetchingPlaces,
    openLocation,
    optionalScrollIntoView,
    combobox.selectedItem,
    setFilterLocation,
  ]);

  const isClearing = useRef(false);

  const inputAttributes = useMemo(() => combobox.getInputProps({
    autoCorrect: 'off',
    autoComplete: 'off',
    spellCheck: 'false',
    placeholder: t('search--search-where-placeholder'),
    ref: refs?.where, // focus management
    name: 'where-input',
    // downshift will combine these handlers with its internal combobox handlers
    onPointerDown: openLocation,
    onFocus: openLocation,
    onKeyDown,
    onBlur: () => {
      setShouldPickFirstMatch(isFetchingPlaces);
      setSubmitFirstMatch(false);
      if (
        !isFetchingPlaces
        && places.length > 1
        && combobox.highlightedIndex === -1
        && !filterLocation?.place_id
      ) {
        setFilterLocation({
          place_id: places[0].place_id,
          description: places[0].description,
        });
      }
    },
  }), [
    combobox,
    t,
    refs?.where,
    openLocation,
    onKeyDown,
    isFetchingPlaces,
    places,
    setFilterLocation,
    filterLocation?.place_id,
  ]);

  const onClear = useCallback(() => {
    isClearing.current = true;
    combobox.selectItem(null);

    // Must be timeout to ensure it opens combo. afterRender and startTransition
    // won't trigger it correctly
    setTimeout(() => {
      refs?.where?.current?.focus();
      isClearing.current = false;
    }, 0);
  }, [combobox, refs?.where]);

  const isSearchNav = variant === 'navigation';

  const showIcon = !isSearchNav;

  const showClear = isLocationOpen && (
    inputAttributes.value || filterLocation?.description
  );

  const showLoading = isLocationOpen && isFetchingPlaces;

  useEffect(() => {
    if (!isFetchingPlaces && shouldPickFirstMatch && combobox.highlightedIndex === -1) {
      setFilterLocation({
        place_id: places[0].place_id,
        description: places[0].description,
      });
      setShouldPickFirstMatch(false);
      setSubmitFirstMatch(false);
      // Allow state to set
      if (shouldSubmitFirstMatch) {
        submitAfter();
      }
    }
    if (combobox.highlightedIndex >= 0) {
      setShouldPickFirstMatch(false);
    }
  }, [
    isFetchingPlaces,
    places,
    setFilterLocation,
    shouldPickFirstMatch,
    shouldSubmitFirstMatch,
    submitAfter,
    inputAttributes.value,
    filterLocation,
    combobox.highlightedIndex,
  ]);

  return (
    <label
      className={cx(styles.label, `${variant}Variant`, {
        isOpen: isLocationOpen || opening === 'location',
      })}
    >
      <div className={cx(styles.icon, { hideIcon: !showIcon })}>
        <Icon
          id={isSearchNav ? 'magnifying-glass' : 'pin'}
          size="l"
          data-test="whereIcon"
        />
      </div>
      <input
        key="search-input"
        {...inputAttributes}
        className={cx(styles.input, `${variant}Variant`, { showIcon: true, hideIcon: isSearchNav, showClear })}
        data-test="whereSearchInput"
        data-cwv="search--where"
      />
      {showLoading && (
        <div className={styles.loading}>
          <LoadingSpinner />
        </div>
      )}
      {showClear && !showLoading && (
        <ClearButton
          onClick={onClear}
          closeIconId={id === 'modal' ? 'x-circle' : 'x'}
        />
      )}
    </label>
  );
};

export default InputLocation;
